import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FastQuoteService } from '@app-services/fast-quote.service';
import { GaEventsService } from '@app-services/ga-events.service';
import { SessionService } from '@app/store/session/session.service';
import { UserInfo } from '@app/store/session/session.store';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { Vehicle } from 'src/app/shared/model/vehicle';
import { AlternativeBidRequest, BidResult, BidStatus, CounterOffer } from 'src/app/shared/services/dealer.service';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'app-make-bid-fast-quote',
  templateUrl: './make-bid-fast-quote.component.html',
  styleUrls: ['./make-bid-fast-quote.component.scss']
})
export class MakeBidFastQuoteComponent implements OnInit, OnChanges {
  private readonly toastr = inject(ToastrService);

  @Input() vehicle: Vehicle;
  @Input() disableConfirmationModal: boolean;
  @Input() fromCard: boolean = false;
  pendingCounterOffer: CounterOffer;
  counterOfferErrorMessage: string;

  loading = false;
  bidErrorMessage: string;
  showLatePaymentLink = false;
  latePaymentQueryParams = { expandedStatuses: '["SOLD_NO_PAYMENT"]' };
  bidValue: number;
  showModal = false;

  rejectedBid: number;
  user: UserInfo;

  forceNewOffer: boolean = false;
  DEFAULT_BID_STEP = 100;

  constructor(
    private fastQuoteService: FastQuoteService,
    private sessionService: SessionService,
    private gaService: GaEventsService,
    private router: Router
  ) { }

  ngOnInit() {
    this.sessionService.getUser()
      .subscribe(user => this.user = user);

    this.bidValue = this.vehicle.bidValue;
    if (!this.bidValue && this.vehicle.highestBid && this.vehicle.isAuction) {
      this.bidValue = this.getNextBidValue();
    }

    this.rejectedBid = this.vehicle.rejectedBidValue;

    if (this.vehicle.counterOffer) {
      this.pendingCounterOffer = this.vehicle.counterOffer;
      // this.pendingCounterOffer.dealerId = this.userInfo.dealerId;
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.vehicle) {
      this.bidValue = this.vehicle.bidValue;
      this.rejectedBid = this.vehicle.rejectedBidValue;
      this.pendingCounterOffer = this.vehicle.counterOffer;
    }
  }

  addToBid(mult: number): void {
    this.bidValue += this.vehicle.bidStep * mult;
  }

  cancelBid() {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.fastQuoteService.cancelBid(this.vehicle.id)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          if (response?.success && response.status === 'OK') {
            this.vehicle.ownsCurrentBid = false;
            this.vehicle.bidValue = undefined;
            this.vehicle.hasBids = false;
            return;
          }

          this.toastr.error(this.resolveBidMessage(response?.status, 'Não foi possível cancelar a oferta.'));
        },
        error: (error: unknown) => {
          this.toastr.error(this.messageFromHttpError(error, 'Não foi possível cancelar a oferta.'));
        }
      });
  }


  makeABid(): void {
    if (this.loading) {
      return;
    }

    if (!this.canBid()) {
      this.redirectToLogin();
      return;
    }

    this.bidErrorMessage = undefined;
    this.showLatePaymentLink = false;
    this.loading = true;
    this.fastQuoteService.addBid(this.vehicle.id, this.bidValue)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (result) => {
          if (result?.success) {
            this.vehicle.bidValue = this.bidValue;
            if (!this.vehicle.hasBids || this.vehicle.highestBid < this.bidValue) {
              this.vehicle.highestBid = this.bidValue;
            }
            this.vehicle.hasBids = true;
            this.vehicle.ownsAnyBid = true;
            this.gaService.fireEvent('Proposta cotação', 'Envio', 'fastQuoteId: ' + this.vehicle.id);
            this.forceNewOffer = false;
            this.closeModal();
            return;
          }

          this.showBidError(this.resolveBidMessage(result?.status, 'Ocorreu um erro ao enviar a oferta.'));
        },
        error: (error: unknown) => {
          this.showBidError(this.messageFromHttpError(error, 'Não foi possível enviar a oferta. Tente novamente.'));
        }
      });
  }

  canBid(): boolean {
    return this.user?.hasRole('DEALER');
  }

  redirectToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: this.router.url
      }
    });
  }

  openModal(): void {
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  isLessThanMin(): boolean {
    return this.vehicle.minBidValue && this.bidValue && this.bidValue < this.vehicle.minBidValue;
  }

  isMoreThanMax(): boolean {
    return this.bidValue && this.bidValue > 999999;
  }

  acceptCounterOffer(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.counterOfferErrorMessage = undefined;
    const bidRequest = this.buildBidRequest();
    this.fastQuoteService.acceptCounterOffer(this.vehicle.id, bidRequest)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (resp: BidResult) => {
          if (resp?.success) {
            this.pendingCounterOffer = undefined;
            location.reload();
            return;
          }

          this.counterOfferErrorMessage = this.resolveBidMessage(resp?.status, 'Não foi possível aceitar a contra proposta.');
        },
        error: (error: unknown) => {
          this.counterOfferErrorMessage = this.messageFromHttpError(error, 'Não foi possível aceitar a contra proposta.');
        }
      });
  }

  rejectCounterOffer(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.counterOfferErrorMessage = undefined;
    const bidRequest = this.buildBidRequest();

    this.fastQuoteService.rejectCounterOffer(this.vehicle.id, bidRequest)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (resp: BidResult) => {
          if (resp?.success) {
            this.pendingCounterOffer = undefined;
            return;
          }

          this.counterOfferErrorMessage = this.resolveBidMessage(resp?.status, 'Não foi possível rejeitar a contra proposta.');
        },
        error: (error: unknown) => {
          this.counterOfferErrorMessage = this.messageFromHttpError(error, 'Não foi possível rejeitar a contra proposta.');
        }
      });
  }

  private showBidError(message: string): void {
    this.bidErrorMessage = message;
    this.showLatePaymentLink = message === BidStatus.LATE_PAYMENT;
  }

  private resolveBidMessage(status: string | null | undefined, fallback: string): string {
    return this.bidStatusLabel(status) ?? this.readableApiText(status) ?? fallback;
  }

  private bidStatusLabel(status: string | null | undefined): string | undefined {
    if (!status || status === 'OK') {
      return undefined;
    }

    const label = BidStatus[status as keyof typeof BidStatus];
    return typeof label === 'string' ? label : undefined;
  }

  private readableApiText(value: string | null | undefined): string | undefined {
    const text = value?.trim();
    if (!text || /^[A-Z0-9_]+$/.test(text)) {
      return undefined;
    }

    return text;
  }

  private messageFromHttpError(error: unknown, fallback: string): string {
    if (!(error instanceof HttpErrorResponse)) {
      return fallback;
    }

    const body: unknown = error.error;
    if (typeof body === 'string') {
      return this.resolveBidMessage(body, fallback);
    }

    if (!body || typeof body !== 'object') {
      return fallback;
    }

    const payload = body as { status?: unknown; message?: unknown };
    const status = typeof payload.status === 'string' ? payload.status : undefined;
    const message = typeof payload.message === 'string' ? payload.message : undefined;

    return this.bidStatusLabel(status)
      ?? this.bidStatusLabel(message)
      ?? this.readableApiText(message)
      ?? this.readableApiText(status)
      ?? fallback;
  }

  private buildBidRequest(): AlternativeBidRequest {
    const result = new AlternativeBidRequest();
    result.counterOffer = this.pendingCounterOffer;
    result.dealerId = this.user.dealerId;
    result.value = this.vehicle.bidValue;

    return result;
  }

  confirmErrorBid(): void {
    this.bidErrorMessage = undefined;
    this.showModal = false;
  }

  clickToMakeNewOffer() {
    if (this.vehicle.isAuction) {
      this.bidValue = this.getNextBidValue();
    }

    this.forceNewOffer = true;
  }

  private getNextBidValue(): number {
    let nextBid = this.vehicle.highestBid + this.DEFAULT_BID_STEP;
    if (this.vehicle.bidStep) {
      nextBid = this.vehicle.highestBid + this.vehicle.bidStep;
    }

    return nextBid;
  }

  calculateCommissionValue(): number {
    if (!this.vehicle.comissionValueConfig) {
      return 0;
    }

    const bidValue = this.bidValue || this.vehicle.bidValue || 0;
    const platesQtd = 1 + (this.vehicle.additionalPlatesQtd || 0);
    
    switch (this.vehicle.comissionValueConfig.type) {
      case 'PERCENTAGE':
        return (bidValue * this.vehicle.comissionValueConfig.value) / 100;
      case 'REAL':
        return (this.vehicle.comissionValueConfig.value || 0) * platesQtd;
      case 'REAL_AND_PERCENTAGE':
        const percentageValue = (bidValue * this.vehicle.comissionValueConfig.value) / 100;
        const additionalFee = (this.vehicle.comissionValueConfig.additionalFee || 0) * platesQtd;
        return additionalFee + percentageValue;
      default:
        return 0;
    }
  }

  calculateTotalOperation(): number {
    const bidValue = this.bidValue || this.vehicle.bidValue || 0;
    return bidValue + this.calculateCommissionValue();
  }

}
